import type {
  PullResult,
  PushResult,
  RemoteChange,
  SnapshotResult,
  Sync,
  SyncOptions,
  SyncState,
  SyncStatus,
  Timers,
} from './types'

export const PUSH_MAX_OPS = 200
export const PULL_LIMIT = 500
export const SNAPSHOT_LIMIT = 500
export const DEBOUNCE_MS = 2_000
export const INTERVAL_MS = 60_000
export const BACKOFF_MIN_MS = 2_000
export const BACKOFF_MAX_MS = 5 * 60_000
// A run still going after this is presumed hung (a reply that stalled mid-body, a refresh that never
// returned): it's abandoned so the next trigger starts a fresh one. Without this one stuck request
// silenced sync until the app was killed (2026-09-24: an hour of work sat on the phone).
export const STALL_MS = 90_000

const realTimers: Timers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (h) => clearInterval(h as ReturnType<typeof setInterval>),
}

// opsSnapshot leaves out track chunks older than 14 days, so their absence says nothing.
// One day of margin covers clock skew between this device and the server.
const CHUNK_PATH = /\/tracks\/(\d{4}-\d{2}-\d{2})\/chunks\//
export function outsideSnapshot(remotePath: string, nowMs: number): boolean {
  const m = CHUNK_PATH.exec(remotePath)
  if (!m) return false
  const day = Date.parse(`${m[1]}T00:00:00Z`)
  return !Number.isFinite(day) || day < nowMs - 13 * 86_400_000
}

function statusOf(err: unknown): number | undefined {
  const s = (err as { status?: unknown } | null)?.status
  return typeof s === 'number' ? s : undefined
}

// Full delay for the nth consecutive failure, before jitter.
export function backoffDelay(failures: number): number {
  return Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** Math.max(0, failures - 1))
}

export function createSync(opts: SyncOptions): Sync {
  const { store, transport, deviceId } = opts
  const now = opts.now ?? Date.now
  const timers = opts.timers ?? realTimers
  const random = opts.random ?? Math.random
  const pushMax = Math.min(opts.limits?.push ?? PUSH_MAX_OPS, PUSH_MAX_OPS)
  const pullLimit = opts.limits?.pull ?? PULL_LIMIT
  const snapshotLimit = opts.limits?.snapshot ?? SNAPSHOT_LIMIT

  let state: SyncState = 'idle'
  let lastSyncAt: number | null = null
  let lastError: string | null = null
  let lastEmitted = ''

  let running: Promise<void> | null = null
  let runningSince = 0
  let generation = 0
  let queued = false
  let failures = 0
  let retryAt = 0
  let started = false
  let stopped = false
  // Battery: the 60 s interval only ticks while the app is on screen AND the network is up.
  // Offline, nothing retries on a timer — the next setOnline(true) wakes the engine.
  let online = true
  let foreground = true
  let lastPushAt = 0

  let debounce: unknown = null
  let retry: unknown = null
  let interval: unknown = null
  let unsubscribe: (() => void) | null = null

  function status(): SyncStatus {
    return { state, ...store.counts(), lastSyncAt, lastError }
  }

  function publish(): void {
    const s = status()
    const key = JSON.stringify(s)
    if (key === lastEmitted) return
    lastEmitted = key
    opts.onStatus?.(s)
  }

  async function call<T>(name: string, data: unknown): Promise<T> {
    try {
      return (await transport.call(name, data)) as T
    } catch (err) {
      if (statusOf(err) !== 401 || !transport.refresh) throw err
      await transport.refresh()
      return (await transport.call(name, data)) as T
    }
  }

  async function push(): Promise<void> {
    lastPushAt = now()
    let lastFirst: string | null = null
    for (;;) {
      const ops = await store.readOutbox(pushMax)
      if (ops.length === 0) return
      // Same head op after a round trip = nothing was settled; stop rather than resend it forever.
      const head = ops[0]!.opId
      if (head === lastFirst) throw new Error('opsSyncPush made no progress on the outbox')
      lastFirst = head
      const groups: { groupId: string; ops: typeof ops }[] = []
      for (const op of ops) {
        const last = groups[groups.length - 1]
        if (last && last.groupId === op.groupId) last.ops.push(op)
        else groups.push({ groupId: op.groupId, ops: [op] })
      }
      const wire = groups.map((g) => ({
        groupId: g.groupId,
        ops: g.ops.map(({ opId, path, mode, data, clientTs }) => ({ opId, path, mode, data, clientTs })),
      }))
      const res = await call<PushResult>('opsSyncPush', { deviceId, groups: wire })

      // An agent session's push can come back Held by the approval gate instead of applied: no
      // results[], just {pendingApproval, approvalId}. Those ops fail out of the outbox (the view drops
      // them); the server applies them itself if the owner approves. Never leave them at the head.
      if (!Array.isArray(res?.results)) {
        const held = (res as unknown as { pendingApproval?: boolean; approvalId?: string } | null)?.pendingApproval
        if (!held) throw new Error('opsSyncPush returned no results for the ops sent')
        const approvalId = (res as unknown as { approvalId?: string }).approvalId ?? ''
        await store.resolveOps({ acks: [], fails: ops.map((op) => ({ opId: op.opId, error: `held:${approvalId}` })) })
        publish()
        continue
      }

      const byId = new Map(res.results.map((r) => [r.opId, r]))
      const acks: string[] = []
      const fails: { opId: string; error: string }[] = []
      for (const op of ops) {
        const r = byId.get(op.opId)
        if (!r) continue
        if (r.status === 'rejected') fails.push({ opId: op.opId, error: r.reason ?? 'rejected' })
        else acks.push(op.opId)
      }
      // A reply that settles nothing would loop forever on the same ops.
      if (acks.length + fails.length === 0) throw new Error('opsSyncPush returned no results for the ops sent')
      await store.resolveOps({ acks, fails })
      publish()
    }
  }

  async function snapshot(): Promise<number> {
    const keep = new Set<string>()
    let pageToken: string | undefined
    for (;;) {
      const page = await call<SnapshotResult>('opsSnapshot', { pageToken, limit: snapshotLimit })
      for (const d of page.docs) keep.add(d.path)
      const changes: RemoteChange[] = page.docs.map((d) => ({ path: d.path, data: d.data }))
      // The cursor lands only with the last page, so a killed snapshot starts over.
      if (page.next === null) {
        await store.applyRemote(changes, { cursor: page.cursor, prune: { keep, skip: (p) => outsideSnapshot(p, now()) } })
        return page.cursor
      }
      await store.applyRemote(changes)
      pageToken = page.next
    }
  }

  async function pull(): Promise<void> {
    let cursor: number | null = await store.getCursor()
    let resynced = cursor === null
    if (cursor === null) cursor = await snapshot()
    for (;;) {
      const res: PullResult = await call<PullResult>('opsSyncPull', { since: cursor, limit: pullLimit })
      if (res.resync) {
        if (resynced) throw new Error('opsSyncPull asked for a resync straight after a snapshot')
        resynced = true
        cursor = await snapshot()
        continue
      }
      const changes: RemoteChange[] = res.changes.map((c) => ({ path: c.path, data: c.kind === 'delete' ? null : c.data }))
      await store.applyRemote(changes, { cursor: res.cursor })
      cursor = res.cursor
      if (!res.more) return
    }
  }

  function clearRetry(): void {
    if (retry !== null) timers.clearTimeout(retry)
    retry = null
  }

  async function runOnce(): Promise<void> {
    state = 'syncing'
    publish()
    try {
      await push()
      await pull()
      failures = 0
      retryAt = 0
      clearRetry()
      state = 'idle'
      lastError = null
      lastSyncAt = now()
    } catch (err) {
      failures++
      const delay = Math.round(backoffDelay(failures) * (0.5 + random() * 0.5))
      retryAt = now() + delay
      state = statusOf(err) === undefined ? 'offline' : 'error'
      lastError = err instanceof Error ? err.message : String(err)
      clearRetry()
      if (started && !stopped && online) {
        retry = timers.setTimeout(() => {
          retry = null
          void run(true)
        }, delay)
      }
    }
    publish()
  }

  function run(force: boolean): Promise<void> {
    if (stopped && !force) return Promise.resolve()
    if (running && now() - runningSince > STALL_MS) {
      // Abandon it: its late finish can't clear the new run (generation check below).
      running = null
      queued = false
      state = 'error'
      lastError = 'sync run stalled — restarted'
    }
    if (running) {
      queued = true
      return running
    }
    if (!force && (!online || now() < retryAt)) return Promise.resolve()
    const gen = ++generation
    runningSince = now()
    running = (async () => {
      try {
        do {
          queued = false
          await runOnce()
        } while (queued && state === 'idle' && !stopped && gen === generation)
      } finally {
        if (gen === generation) {
          queued = false
          running = null
        }
      }
    })()
    return running
  }

  function armInterval(): void {
    if (interval !== null || !started || stopped || !online || !foreground) return
    interval = timers.setInterval(() => void run(false), INTERVAL_MS)
  }

  function disarmInterval(): void {
    if (interval !== null) timers.clearInterval(interval)
    interval = null
  }

  // Push only — for background wakes, where a pull would spend radio and CPU on data nobody
  // is looking at. Skips when offline, when nothing is waiting, inside a backoff, or when the
  // last push was under minGapMs ago.
  async function flush(minGapMs: number): Promise<void> {
    if (running && now() - runningSince > STALL_MS) running = null
    if (running) await running
    if (!online || store.counts().pending === 0 || now() < retryAt) return
    if (minGapMs > 0 && now() - lastPushAt < minGapMs) return
    const gen = ++generation
    runningSince = now()
    running = (async () => {
      try {
        state = 'syncing'
        publish()
        await push()
        failures = 0
        retryAt = 0
        state = 'idle'
        lastError = null
        lastSyncAt = now()
      } catch (err) {
        failures++
        retryAt = now() + Math.round(backoffDelay(failures) * (0.5 + random() * 0.5))
        state = statusOf(err) === undefined ? 'offline' : 'error'
        lastError = err instanceof Error ? err.message : String(err)
      } finally {
        if (gen === generation) running = null
        publish()
      }
    })()
    return running
  }

  function setOnline(up: boolean): void {
    if (up === online) return
    online = up
    if (!up) {
      disarmInterval()
      clearRetry()
      if (debounce !== null) timers.clearTimeout(debounce)
      debounce = null
      if (!running) state = 'offline'
      publish()
      return
    }
    armInterval()
    void run(true)
  }

  function setForeground(fg: boolean): void {
    if (fg === foreground) return
    foreground = fg
    if (fg) {
      armInterval()
      void run(false)
      return
    }
    disarmInterval()
    if (debounce !== null) timers.clearTimeout(debounce)
    debounce = null
    void flush(0)
  }

  return {
    status,

    async syncNow() {
      // The run (or the one it queues) reads the whole outbox — a pending debounce would only repeat it.
      if (debounce !== null) timers.clearTimeout(debounce)
      debounce = null
      await run(true)
      return status()
    },

    start(startOpts) {
      if (started) return
      started = true
      stopped = false
      if (startOpts?.foreground !== undefined) foreground = startOpts.foreground
      unsubscribe = store.onOutboxChange(() => {
        publish()
        if (!online) return
        if (debounce !== null) timers.clearTimeout(debounce)
        debounce = timers.setTimeout(() => {
          debounce = null
          void run(false)
        }, DEBOUNCE_MS)
      })
      armInterval()
      void run(false)
    },

    stop() {
      stopped = true
      started = false
      unsubscribe?.()
      unsubscribe = null
      if (debounce !== null) timers.clearTimeout(debounce)
      disarmInterval()
      clearRetry()
      debounce = null
    },

    setOnline,
    setForeground,

    flush(minGapMs = 0) {
      return flush(minGapMs).then(status)
    },

    notifyOnline() {
      if (!online) setOnline(true)
      else void run(true)
    },

    notifyForeground() {
      if (!foreground) setForeground(true)
      else void run(false)
    },
  }
}
